import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { GetFhirRecordParams } from '@models/fhir-resource';
import { ImmunizationRecord } from '@models/immunization';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListImmunizationsQuery,
  useGetImmunizationQuery,
  usePostImmunizationRecordMutation,
  useUpdateImmunizationRecordMutation,
  useDeleteImmunizationRecordMutation,
} from 'generated/immunization.generated';
import type {
  PostImmunizationRecordMutationVariables,
  UpdateImmunizationRecordMutationVariables,
  DeleteImmunizationRecordMutation,
  DeleteImmunizationRecordMutationVariables,
} from 'generated/immunization.generated';
import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';

export const useGetImmunizations = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListImmunizationsQuery({
    variables: { input: { patient: patient_id, count: 1500, page: 1 } },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listImmunizations.data ?? []).map(
    (r) => new ImmunizationRecord(r)
  );
  return { ...response, data: records };
};

export const useGetImmunization = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetImmunizationQuery({
    variables: { input: { record_id: params.record_id, patient_id } },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getImmunization;
  return { ...response, data: record ? new ImmunizationRecord(record) : null };
};

export function useCreateImmunization(): [
  (
    params: PostImmunizationRecordMutationVariables
  ) => Promise<FetchResult<ImmunizationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostImmunizationRecordMutation();
  const loading = baseLoading;

  async function createImmunization(
    params: PostImmunizationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postImmunizationRecord) {
          return;
        }
        const storeObject =
          response.postImmunizationRecord as unknown as StoreObject;
        const { immunization } = params.input;
        try {
          cache.modify<{ listImmunizations: CachedRecordList }>({
            fields: {
              listImmunizations: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === immunization.patient.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });

          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: immunization.patient.reference,
                },
                immunization: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                immunization: [
                  ...(data?.getPatientHealthSummaryRecords.immunization ?? []),
                  response.postImmunizationRecord,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });

    const createdImmun = response.data?.postImmunizationRecord;
    return {
      ...response,
      data: createdImmun ? new ImmunizationRecord(createdImmun) : undefined,
    };
  }
  return [createImmunization, loading];
}

export function useUpdateImmunization(): [
  (
    params: UpdateImmunizationRecordMutationVariables
  ) => Promise<FetchResult<ImmunizationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateImmunizationRecordMutation();
  const loading = baseLoading;

  async function updateImmunization(
    params: UpdateImmunizationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedImmun = response.data?.updateImmunizationRecord;
    return {
      ...response,
      data: updatedImmun ? new ImmunizationRecord(updatedImmun) : undefined,
    };
  }
  return [updateImmunization, loading];
}

export function useDeleteImmunization(): [
  (
    params: DeleteImmunizationRecordMutationVariables
  ) => Promise<FetchResult<DeleteImmunizationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteImmunizationRecordMutation();
  const loading = baseLoading;

  async function deleteImmunization(
    params: DeleteImmunizationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteImmunizationRecord) {
          return;
        }
        const { immunization_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'ImmunizationModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteImmunization, loading];
}
